
import DOMPurify from 'dompurify';
import { ValidationError } from '@/utils/errors';

// UUID validation regex (RFC 4122 compliant)
const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// Check if a string is a valid UUID
export const isValidUUID = (id: string): boolean => {
  return UUID_REGEX.test(id);
};

// Validate UUID format, throws ValidationError if invalid
export const validateUUID = (id: string, fieldName = 'ID'): void => {
  if (!isValidUUID(id)) {
    throw new ValidationError(`Invalid ${fieldName} format: ${id}`, fieldName);
  }
};

// Sanitize HTML content to prevent XSS
export const sanitizeHtml = (html: string): string => {
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: [], // No HTML tags allowed
    ALLOWED_ATTR: [],
    KEEP_CONTENT: true, // Keep text content but remove HTML
  });
};

// Sanitize text input
export const sanitizeText = (text: string): string => {
  if (!text) return '';
  
  // Remove any HTML tags and decode HTML entities
  const sanitized = sanitizeHtml(text);
  
  // Trim whitespace and limit length
  return sanitized.trim().slice(0, 1000);
};

// Common sanitize helper: sanitize text, apply character filter, and limit length
const sanitizeWithPattern = (input: string, allowedCharsRegex: RegExp, maxLength: number): string => {
  if (!input) return '';
  const sanitized = sanitizeText(input);
  return sanitized.replace(allowedCharsRegex, '').slice(0, maxLength);
};

// Sanitize book title
export const sanitizeBookTitle = (title: string): string =>
  sanitizeWithPattern(title, /[^\w\s\-.,!?'":()]/g, 200);

// Sanitize author name
export const sanitizeAuthorName = (name: string): string =>
  sanitizeWithPattern(name, /[^a-zA-Z\s.\-]/g, 100);

// Sanitize description
export const sanitizeDescription = (description: string): string =>
  sanitizeWithPattern(description, /[<>{}]/g, 500);

// Validate and sanitize price
export const sanitizePrice = (price: string | number): number => {
  const numPrice = typeof price === 'string' ? parseFloat(price) : price;
  
  if (isNaN(numPrice) || numPrice < 0) return 0;
  if (numPrice > 100000) return 100000; // Max price limit
  
  return Math.round(numPrice * 100) / 100; // Round to 2 decimal places
};

// Sanitize email address (pure, non-throwing - validation handled by rules)
export const sanitizeEmail = (email: string): string => {
  if (!email) return '';
  
  const sanitized = sanitizeText(email);
  
  // Just normalize: trim, lowercase, limit length
  // Format validation is handled by pattern/custom rules
  return sanitized.toLowerCase().slice(0, 254); // RFC 5321 limit
};

// Sanitize phone number
export const sanitizePhoneNumber = (phone: string): string => {
  if (!phone) return '';
  
  const sanitized = sanitizeText(phone);
  
  // Remove all non-digit characters except +
  return sanitized.replace(/[^+\d]/g, '').slice(0, 20);
};

// Sanitize URL (pure, non-throwing - validation handled by rules)
export const sanitizeUrl = (url: string): string => {
  if (!url) return '';
  
  const sanitized = sanitizeText(url);
  
  // Just normalize: trim and limit length
  // Format validation is handled by pattern/custom rules
  return sanitized.slice(0, 2048); // Common URL length limit
};
